import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Stage, Crest } from "@/components/bigscreen/Scoreboard";
import { screenClock, isLive, goalSide, minuteLabel, GOAL_TYPES } from "@/lib/bigScreen";

const REFRESH_MS = 30000;

const EventList = ({ events, match, side }) => {
  const goals = events.filter((e) => GOAL_TYPES.includes(e.type) && goalSide(e, match) === side);
  const cards = events.filter((e) => ["yellow_card", "red_card"].includes(e.type) && (e.team_id === match[`${side}_team_id`]));
  const align = side === "home" ? "items-end text-right" : "items-start text-left";
  return (
    <div className={`flex flex-col gap-1 ${align} min-w-0`} data-testid={`stadium-events-${side}`}>
      {goals.slice(-4).map((g) => (
        <p key={g.event_id} className="text-[34px] font-semibold text-white truncate max-w-full">
          <span className="inline-block w-4 h-4 rounded-full bg-[#FFC72C] mr-3 align-middle" />{g.player_name || "Goal"} {minuteLabel(g)}{g.type === "penalty_goal" ? " (P)" : g.type === "own_goal" ? " (OG)" : ""}
        </p>
      ))}
      {cards.slice(-4).map((c) => (
        <p key={c.event_id} className="text-[28px] text-white/80 flex items-center gap-3 truncate max-w-full" data-testid={`stadium-card-${c.event_id}`}>
          <span className={`inline-block w-5 h-7 rounded-sm ${c.type === "red_card" ? "bg-[#E10600]" : "bg-[#FFD400]"}`} />
          {c.player_name || "Card"} {minuteLabel(c)}
        </p>
      ))}
    </div>
  );
};

const Lineup = ({ lineup, team, side }) => (
  <div className="bg-white/[0.05] ring-1 ring-white/10 px-8 py-5 min-w-0" data-testid={`stadium-lineup-${side}`}>
    <p className="font-heading font-black uppercase text-[30px] text-[#4FD1E8] tracking-widest mb-3 truncate">
      {team?.short_name || team?.name} {lineup?.formation ? `· ${lineup.formation}` : ""}
    </p>
    {lineup?.starters?.length ? (
      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
        {lineup.starters.slice(0, 11).map((p, i) => (
          <p key={p.player_id || i} className="text-[28px] text-white flex items-center gap-3 truncate">
            <span className="w-12 text-right font-heading font-black text-[#FFC72C] tabular-nums">{p.number ?? ""}</span>
            <span className="truncate">{p.player_name}</span>
            {p.position === "GK" && <span className="text-[16px] font-bold px-1.5 bg-[#FFC72C] text-black">GK</span>}
          </p>
        ))}
      </div>
    ) : <p className="text-[28px] text-white/40">Line-up not yet announced</p>}
  </div>
);

const StadiumViewPage = () => {
  const { id } = useParams();
  const [centre, setCentre] = useState(null);
  const [error, setError] = useState(null);
  const [skew, setSkew] = useState({ current: 0 });
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const t0 = Date.now();
      const [c, clk] = await Promise.all([axios.get(`${API}/matches/${id}/centre`), axios.get(`${API}/matches/${id}/clock`)]);
      if (clk.data?.server_time) setSkew({ current: new Date(clk.data.server_time).getTime() + (Date.now() - t0) / 2 - Date.now() });
      setCentre({ ...c.data, clock: clk.data });
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "Match not found" : "Reconnecting…");
    }
  }, [id]);

  useEffect(() => {
    document.title = "ABFA Stadium View";
    load();
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [load]);

  if (!centre) {
    return <Stage><div className="flex-1 flex items-center justify-center text-[60px] font-heading font-black text-white/60" data-testid="stadium-loading">{error || "Loading match…"}</div></Stage>;
  }

  const { match, home_team: home, away_team: away, events = [], clock, competition } = centre;
  const c = screenClock(clock, match, skew.current);
  const live = isLive(match, clock);
  const scored = match.home_score != null || match.status === "completed";

  return (
    <div data-testid="stadium-view-page">
      <Stage>
        <div className="flex items-center justify-between h-[56px]">
          <p className="font-heading font-bold uppercase tracking-[0.2em] text-[30px] text-white/70 truncate" data-testid="stadium-competition">{competition?.name}</p>
          {live && <span className="px-5 py-1 bg-[#E10600] text-white font-heading font-black text-[28px] tracking-[0.2em]" data-testid="stadium-live">LIVE</span>}
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-8 mt-4">
          <div className="flex items-center justify-end gap-6 min-w-0">
            <p className="font-heading font-black uppercase text-white text-[64px] leading-none text-right truncate" data-testid="stadium-home-name">{home?.name}</p>
            <Crest team={home} size={170} />
          </div>
          <div className="flex flex-col items-center">
            <p className="font-heading font-black text-white text-[220px] leading-none tabular-nums" data-testid="stadium-score">
              {scored ? `${match.home_score ?? 0}-${match.away_score ?? 0}` : "VS"}
            </p>
            <p className="font-heading font-black text-[72px] text-[#FFC72C] tabular-nums leading-none mt-2" data-testid="stadium-clock">
              {c.main}{c.added && <span className="text-[48px] ml-3">{c.added}</span>}
            </p>
            <p className="font-heading font-bold uppercase tracking-[0.3em] text-[26px] text-[#4FD1E8] mt-2" data-testid="stadium-period">{c.period}</p>
          </div>
          <div className="flex items-center gap-6 min-w-0">
            <Crest team={away} size={170} />
            <p className="font-heading font-black uppercase text-white text-[64px] leading-none truncate" data-testid="stadium-away-name">{away?.name}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-16 mt-6 min-h-[150px]">
          <EventList events={events} match={match} side="home" />
          <EventList events={events} match={match} side="away" />
        </div>

        <div className="grid grid-cols-2 gap-8 mt-auto">
          <Lineup lineup={centre.lineup_home} team={home} side="home" />
          <Lineup lineup={centre.lineup_away} team={away} side="away" />
        </div>
        {error && <p className="absolute bottom-0 right-0 text-[18px] text-white/40" data-testid="stadium-error">{error}</p>}
      </Stage>
    </div>
  );
};

export default StadiumViewPage;
